import React, { useMemo, useState } from 'react';
import Footer from './Footer';
import NavLinks from './NavLinks';
import { getSiteTheme, getStoredDarkMode, persistDarkMode } from '../styles/siteTheme';

const PAGE_CONTAINER = {
  width: '100%',
  maxWidth: 1100,
  margin: '0 auto',
  padding: '0 16px'
};

/**
 * Shared chrome for Login / Signup: themed nav, glow, footer.
 * `children` is a render prop: (ctx) => ReactNode where ctx has theme, darkMode, shellStyles.
 */
function AuthLayout({ brandTitle, navRightSlot, children }) {
  const [darkMode, setDarkMode] = useState(getStoredDarkMode);

  const toggleTheme = () => {
    setDarkMode((prev) => {
      const next = !prev;
      persistDarkMode(next);
      return next;
    });
  };

  const theme = useMemo(() => getSiteTheme(darkMode), [darkMode]);

  const shellStyles = useMemo(() => {
    const buttonBase = {
      appearance: 'none',
      border: `1px solid ${theme.border2}`,
      borderRadius: 12,
      padding: '10px 14px',
      fontSize: 14,
      fontWeight: 600,
      lineHeight: 1,
      color: theme.text,
      background: 'transparent',
      cursor: 'pointer',
      transition: 'transform 120ms ease, background 120ms ease, border-color 120ms ease'
    };

    return {
      page: {
        minHeight: '100vh',
        background: theme.bg,
        color: theme.text,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflowX: 'hidden'
      },
      glow: {
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        background: theme.glowBg
      },
      nav: {
        position: 'sticky',
        top: 0,
        zIndex: 50,
        borderBottom: `1px solid ${theme.border}`,
        background: theme.navBg,
        backdropFilter: 'blur(14px)'
      },
      navInner: {
        ...PAGE_CONTAINER,
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto',
        alignItems: 'center',
        gap: 12,
        paddingTop: 14,
        paddingBottom: 14,
        minHeight: 52
      },
      brand: {
        display: 'flex',
        alignItems: 'baseline',
        gap: 10,
        cursor: 'pointer',
        userSelect: 'none'
      },
      brandTitle: {
        fontSize: 16,
        fontWeight: 800,
        letterSpacing: '-0.02em'
      },
      brandBadge: {
        fontSize: 12,
        color: theme.muted,
        padding: '4px 10px',
        borderRadius: 999,
        border: `1px solid ${theme.border}`,
        background: theme.brandBadgeBg
      },
      navLinks: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 14,
        flexWrap: 'wrap'
      },
      navLink: {
        color: theme.muted,
        textDecoration: 'none',
        fontSize: 14,
        fontWeight: 600
      },
      navCtas: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        justifyContent: 'flex-end',
        flexWrap: 'wrap'
      },
      btnGhost: {
        ...buttonBase,
        background: theme.btnGhostBg
      },
      btnPrimary: {
        ...buttonBase,
        borderColor: darkMode ? 'rgba(96, 165, 250, 0.35)' : 'rgba(59, 130, 246, 0.4)',
        background: darkMode
          ? 'linear-gradient(180deg, rgba(96, 165, 250, 0.24), rgba(59, 130, 246, 0.16))'
          : 'linear-gradient(180deg, #3B82F6, #2563EB)',
        color: theme.btnPrimaryColor
      },
      main: {
        flex: 1,
        width: '100%',
        padding: '48px 0 40px'
      },
      pageInner: PAGE_CONTAINER,
      footer: {
        borderTop: `1px solid ${theme.border}`,
        background: theme.footerBg,
        color: theme.muted,
        textAlign: 'center',
        padding: '16px 20px',
        fontSize: 14,
        '--rs-primary-500': theme.primary,
        '--rs-text-secondary': theme.muted,
        '--rs-border-secondary': theme.border
      }
    };
  }, [theme, darkMode]);

  const ctx = useMemo(
    () => ({
      theme,
      darkMode,
      toggleTheme,
      shellStyles
    }),
    [theme, darkMode, shellStyles]
  );

  return (
    <div style={shellStyles.page}>
      <div style={shellStyles.glow} />

      <nav style={shellStyles.nav} aria-label="Primary">
        <div style={shellStyles.navInner}>
          <div
            style={shellStyles.brand}
            role="button"
            tabIndex={0}
            onClick={() => { window.location.hash = '#/'; }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') window.location.hash = '#/';
            }}
          >
            <div style={shellStyles.brandTitle}>{brandTitle}</div>
            <button
              type="button"
              style={{
                ...shellStyles.brandBadge,
                cursor: 'pointer',
                margin: 0,
                font: 'inherit'
              }}
              onClick={(e) => {
                e.stopPropagation();
                toggleTheme();
              }}
              aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {darkMode ? 'Dark' : 'Light'}
            </button>
          </div>

          <div style={shellStyles.navLinks}>
            <NavLinks navLinkStyle={shellStyles.navLink} theme={theme} />
          </div>

          <div style={shellStyles.navCtas}>{navRightSlot(shellStyles)}</div>
        </div>
      </nav>

      <main style={shellStyles.main}>{children(ctx)}</main>

      <Footer compact style={shellStyles.footer} />
    </div>
  );
}

export default AuthLayout;
